import clsx from 'clsx';
import { useTranslation } from '@client-app/i18n';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useEffect, useState, type KeyboardEvent } from 'react';
import Modal from '../Modal';
import IntegrationConnectGuide, { getIntegrationConnectGuideModalWidth } from './IntegrationConnectGuide';

interface IntegrationApiKeyModalProps {
    isOpen: boolean;
    onClose: () => void;
    toolName: string;
    apiKey: string;
    onApiKeyChange: (value: string) => void;
    /** @deprecated Business-group assignment occurs after connection succeeds. */
    accountLabel?: string;
    /** @deprecated Business-group assignment occurs after connection succeeds. */
    onAccountLabelChange?: (value: string) => void;
    storeUrl?: string;
    onStoreUrlChange?: (value: string) => void;
    onConfirm: () => void;
    isLoading?: boolean;
    /** @deprecated Business-group assignment occurs after connection succeeds. */
    brandOptions?: unknown[];
    selectedBrandIds?: string[];
    onSelectedBrandIdsChange?: (brandIds: string[]) => void;
}

function splitOkendoCredentials(credentials: string): { userId: string; merchantApiKey: string } {
    const trimmed = credentials.trim();
    const separatorIndex = trimmed.indexOf(':');
    if (separatorIndex < 0) {
        return { userId: '', merchantApiKey: trimmed };
    }
    return {
        userId: trimmed.slice(0, separatorIndex).trim(),
        merchantApiKey: trimmed.slice(separatorIndex + 1).trim()
    };
}

function formatOkendoCredentials(userId: string, merchantApiKey: string): string {
    const trimmedUserId = userId.trim();
    const trimmedMerchantApiKey = merchantApiKey.trim();
    if (!trimmedUserId && !trimmedMerchantApiKey) return '';
    return `${trimmedUserId}:${trimmedMerchantApiKey}`;
}

function splitLooxCredentials(credentials: string): { publicStoreId: string; secretKey: string } {
    const trimmed = credentials.trim();
    const separatorIndex = trimmed.indexOf(':');
    if (separatorIndex < 0) {
        return { publicStoreId: '', secretKey: trimmed };
    }
    return {
        publicStoreId: trimmed.slice(0, separatorIndex).trim(),
        secretKey: trimmed.slice(separatorIndex + 1).trim()
    };
}

function formatLooxCredentials(publicStoreId: string, secretKey: string): string {
    const trimmedPublicStoreId = publicStoreId.trim();
    const trimmedSecretKey = secretKey.trim();
    if (!trimmedPublicStoreId && !trimmedSecretKey) return '';
    return `${trimmedPublicStoreId}:${trimmedSecretKey}`;
}

export default function IntegrationApiKeyModal({
    isOpen,
    onClose,
    toolName,
    apiKey,
    onApiKeyChange,
    storeUrl = '',
    onStoreUrlChange,
    onConfirm,
    isLoading = false,
}: IntegrationApiKeyModalProps) {
    const { t } = useTranslation();
    const [showPrestaShopWebserviceKey, setShowPrestaShopWebserviceKey] = useState(false);
    const [showOkendoMerchantApiKey, setShowOkendoMerchantApiKey] = useState(false);
    const [showLooxSecretKey, setShowLooxSecretKey] = useState(false);
    const [showGoAffProAdminToken, setShowGoAffProAdminToken] = useState(false);
    const [showAttribulyApiKey, setShowAttribulyApiKey] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setShowPrestaShopWebserviceKey(false);
            setShowOkendoMerchantApiKey(false);
            setShowLooxSecretKey(false);
            setShowGoAffProAdminToken(false);
            setShowAttribulyApiKey(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const isPrestaShop = toolName === 'PrestaShop';
    const isOkendo = toolName === 'Okendo';
    const isLoox = toolName === 'Loox';
    const isGoAffPro = toolName === 'GoAffPro';
    const isNoxInfluencer = toolName === 'NoxInfluencer';
    const isAttribuly = toolName === 'Attribuly';
    const okendoCredentials = splitOkendoCredentials(apiKey);
    const looxCredentials = splitLooxCredentials(apiKey);
    const hasConnectionDetails = isPrestaShop
        ? Boolean(storeUrl.trim() && apiKey.trim())
        : isOkendo
            ? Boolean(okendoCredentials.userId && okendoCredentials.merchantApiKey)
        : isLoox
            ? Boolean(looxCredentials.publicStoreId && looxCredentials.secretKey)
        : Boolean(apiKey.trim());
    const canConfirm = hasConnectionDetails;
    const modalWidth = getIntegrationConnectGuideModalWidth(toolName, 'max-w-lg');

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canConfirm && !isLoading) {
            onConfirm();
        }
    };

    const handleOkendoUserIdChange = (value: string) => {
        onApiKeyChange(formatOkendoCredentials(value, okendoCredentials.merchantApiKey));
    };

    const handleOkendoMerchantApiKeyChange = (value: string) => {
        onApiKeyChange(formatOkendoCredentials(okendoCredentials.userId, value));
    };

    const handleLooxPublicStoreIdChange = (value: string) => {
        onApiKeyChange(formatLooxCredentials(value, looxCredentials.secretKey));
    };

    const handleLooxSecretKeyChange = (value: string) => {
        onApiKeyChange(formatLooxCredentials(looxCredentials.publicStoreId, value));
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('website.integrationsSettings.connectToolTitle', { tool: toolName })}
            width={modalWidth}
            bodyClassName="px-6 pb-6 pt-1"
            titleClassName="text-[#1A162F]"
        >
            <div className="space-y-4">
                {isPrestaShop ? (
                    <>
                        <IntegrationConnectGuide toolName={toolName} />

                        <p className="text-sm leading-6 text-[#666F8D]">
                            {t('website.integrationsSettings.prestashopCredentialDescription')}
                        </p>
                        <div>
                            <label
                                htmlFor="prestashop-store-url"
                                className="mb-2 block text-sm font-semibold text-[#1A162F]"
                            >
                                {t('website.integrationsSettings.prestashopStoreUrl')}
                            </label>
                            <input
                                id="prestashop-store-url"
                                name="prestashop-store-url"
                                type="url"
                                autoComplete="url"
                                spellCheck={false}
                                value={storeUrl}
                                onChange={(e) => onStoreUrlChange?.(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={t('website.integrationsSettings.prestashopStoreUrlPlaceholder')}
                                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-[#1A162F] outline-none placeholder:text-[#666F8D] transition-all focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F]"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="prestashop-webservice-key"
                                className="mb-2 block text-sm font-semibold text-[#1A162F]"
                            >
                                {t('website.integrationsSettings.prestashopWebserviceKey')}
                            </label>
                            <div className="relative">
                                <input
                                    id="prestashop-webservice-key"
                                    name="prestashop-webservice-key"
                                    type={showPrestaShopWebserviceKey ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    spellCheck={false}
                                    value={apiKey}
                                    onChange={(e) => onApiKeyChange(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t('website.integrationsSettings.prestashopWebserviceKeyPlaceholder')}
                                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 pr-11 text-sm text-[#1A162F] outline-none placeholder:text-[#666F8D] transition-all focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/20"
                                />
                                <button
                                    type="button"
                                    aria-label={showPrestaShopWebserviceKey
                                        ? t('website.integrationsSettings.hidePrestaShopWebserviceKey')
                                        : t('website.integrationsSettings.showPrestaShopWebserviceKey')}
                                    title={showPrestaShopWebserviceKey
                                        ? t('website.integrationsSettings.hidePrestaShopWebserviceKey')
                                        : t('website.integrationsSettings.showPrestaShopWebserviceKey')}
                                    onClick={() => setShowPrestaShopWebserviceKey((visible) => !visible)}
                                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] focus:outline-none focus:ring-2 focus:ring-[#1A162F]/20"
                                >
                                    {showPrestaShopWebserviceKey ? (
                                        <EyeSlash size={18} weight="regular" />
                                    ) : (
                                        <Eye size={18} weight="regular" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </>
                ) : isOkendo ? (
                    <>
                        <IntegrationConnectGuide toolName={toolName} />

                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-[#1A162F]">
                                    {t('website.integrationsSettings.okendoUserId')}
                                </label>
                                <input
                                    type="text"
                                    value={okendoCredentials.userId}
                                    onChange={(e) => handleOkendoUserIdChange(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t('website.integrationsSettings.okendoUserIdPlaceholder')}
                                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-[#1A162F] outline-none placeholder:text-[#666F8D] transition-all focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F]"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-[#1A162F]">
                                    {t('website.integrationsSettings.okendoMerchantRestApiKey')}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showOkendoMerchantApiKey ? 'text' : 'password'}
                                        value={okendoCredentials.merchantApiKey}
                                        onChange={(e) => handleOkendoMerchantApiKeyChange(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder={t('website.integrationsSettings.okendoMerchantRestApiKeyPlaceholder')}
                                        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 pr-11 text-sm text-[#1A162F] outline-none placeholder:text-[#666F8D] transition-all focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F]"
                                    />
                                    <button
                                        type="button"
                                        aria-label={showOkendoMerchantApiKey
                                            ? t('website.integrationsSettings.hideOkendoMerchantRestApiKey')
                                            : t('website.integrationsSettings.showOkendoMerchantRestApiKey')}
                                        title={showOkendoMerchantApiKey
                                            ? t('website.integrationsSettings.hideOkendoMerchantRestApiKey')
                                            : t('website.integrationsSettings.showOkendoMerchantRestApiKey')}
                                        onClick={() => setShowOkendoMerchantApiKey((visible) => !visible)}
                                        className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] focus:outline-none focus:ring-2 focus:ring-[#1A162F]/20"
                                    >
                                        {showOkendoMerchantApiKey ? (
                                            <EyeSlash size={18} weight="regular" />
                                        ) : (
                                            <Eye size={18} weight="regular" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                ) : isLoox ? (
                    <>
                        <IntegrationConnectGuide toolName={toolName} />

                        <p className="text-sm leading-6 text-[#666F8D]">
                            {t('website.integrationsSettings.looxCredentialDescription')}
                        </p>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-[#1A162F]">
                                    {t('website.integrationsSettings.looxPublicStoreId')}
                                </label>
                                <input
                                    type="text"
                                    autoComplete="off"
                                    spellCheck={false}
                                    value={looxCredentials.publicStoreId}
                                    onChange={(e) => handleLooxPublicStoreIdChange(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t('website.integrationsSettings.looxPublicStoreIdPlaceholder')}
                                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-[#1A162F] outline-none placeholder:text-[#666F8D] transition-all focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F]"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-[#1A162F]">
                                    {t('website.integrationsSettings.looxSecretApiKey')}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showLooxSecretKey ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        spellCheck={false}
                                        value={looxCredentials.secretKey}
                                        onChange={(e) => handleLooxSecretKeyChange(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder={t('website.integrationsSettings.looxSecretApiKeyPlaceholder')}
                                        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 pr-11 text-sm text-[#1A162F] outline-none placeholder:text-[#666F8D] transition-all focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F]"
                                    />
                                    <button
                                        type="button"
                                        aria-label={showLooxSecretKey
                                            ? t('website.integrationsSettings.hideLooxSecretApiKey')
                                            : t('website.integrationsSettings.showLooxSecretApiKey')}
                                        title={showLooxSecretKey
                                            ? t('website.integrationsSettings.hideLooxSecretApiKey')
                                            : t('website.integrationsSettings.showLooxSecretApiKey')}
                                        onClick={() => setShowLooxSecretKey((visible) => !visible)}
                                        className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] focus:outline-none focus:ring-2 focus:ring-[#1A162F]/20"
                                    >
                                        {showLooxSecretKey ? (
                                            <EyeSlash size={18} weight="regular" />
                                        ) : (
                                            <Eye size={18} weight="regular" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                ) : isGoAffPro ? (
                    <>
                        <IntegrationConnectGuide toolName={toolName} />

                        <p className="text-sm leading-6 text-[#666F8D]">
                            {t('website.integrationsSettings.goAffProCredentialDescription')}
                        </p>
                        <div>
                            <label className="mb-2 block text-sm font-semibold text-[#1A162F]">
                                {t('website.integrationsSettings.goAffProAdminAccessToken')}
                            </label>
                            <div className="relative">
                                <input
                                    type={showGoAffProAdminToken ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    spellCheck={false}
                                    value={apiKey}
                                    onChange={(e) => onApiKeyChange(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t('website.integrationsSettings.goAffProAdminAccessTokenPlaceholder')}
                                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 pr-11 text-sm text-[#1A162F] outline-none placeholder:text-[#666F8D] transition-all focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F]"
                                />
                                <button
                                    type="button"
                                    aria-label={showGoAffProAdminToken
                                        ? t('website.integrationsSettings.hideGoAffProAdminAccessToken')
                                        : t('website.integrationsSettings.showGoAffProAdminAccessToken')}
                                    title={showGoAffProAdminToken
                                        ? t('website.integrationsSettings.hideGoAffProAdminAccessToken')
                                        : t('website.integrationsSettings.showGoAffProAdminAccessToken')}
                                    onClick={() => setShowGoAffProAdminToken((visible) => !visible)}
                                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] focus:outline-none focus:ring-2 focus:ring-[#1A162F]/20"
                                >
                                    {showGoAffProAdminToken ? <EyeSlash size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <IntegrationConnectGuide toolName={toolName} />

                        {isNoxInfluencer ? (
                            <p className="text-sm leading-6 text-[#666F8D]">
                                {t('website.integrationsSettings.noxInfluencerCredentialDescription')}
                            </p>
                        ) : null}

                        <div>
                            <label className="mb-2 block text-sm font-semibold text-[#1A162F]">
                                {isNoxInfluencer
                                    ? t('website.integrationsSettings.noxKey')
                                    : t('website.integrationsSettings.apiKey')}
                            </label>
                            {isAttribuly ? (
                                <div className="relative">
                                    <input
                                        type={showAttribulyApiKey ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        spellCheck={false}
                                        value={apiKey}
                                        onChange={(e) => onApiKeyChange(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder={t('website.integrationsSettings.apiKeyPlaceholder', { tool: toolName })}
                                        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 pr-11 text-sm text-[#1A162F] outline-none placeholder:text-[#666F8D] transition-all focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/20"
                                    />
                                    <button
                                        type="button"
                                        aria-label={showAttribulyApiKey
                                            ? t('website.integrationsSettings.hideApiKey', { tool: toolName })
                                            : t('website.integrationsSettings.showApiKey', { tool: toolName })}
                                        title={showAttribulyApiKey
                                            ? t('website.integrationsSettings.hideApiKey', { tool: toolName })
                                            : t('website.integrationsSettings.showApiKey', { tool: toolName })}
                                        onClick={() => setShowAttribulyApiKey((visible) => !visible)}
                                        className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] focus:outline-none focus:ring-2 focus:ring-[#1A162F]/20"
                                    >
                                        {showAttribulyApiKey ? <EyeSlash size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            ) : (
                                <textarea
                                    value={apiKey}
                                    onChange={(e) => onApiKeyChange(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={isNoxInfluencer
                                        ? t('website.integrationsSettings.noxKeyPlaceholder')
                                        : t('website.integrationsSettings.apiKeyPlaceholder', { tool: toolName })}
                                    rows={4}
                                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-[#1A162F] outline-none placeholder:text-[#666F8D] transition-all focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F]"
                                />
                            )}
                        </div>
                    </>
                )}

                <div className="flex justify-end gap-3 pt-2">
                    <button
                        onClick={onClose}
                        className="rounded-lg px-4 py-2 text-sm font-medium text-[#666F8D] transition-colors hover:bg-gray-100 hover:text-[#1A162F]"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={!canConfirm || isLoading}
                        className={clsx(
                            "rounded-lg px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-2",
                            canConfirm && !isLoading
                                ? "bg-[#1A162F] text-white hover:bg-[#1A162F]/90"
                                : "cursor-not-allowed bg-gray-300 text-[#666F8D]"
                        )}
                    >
                        {isLoading ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : null}
                        {t('website.integrationsSettings.connectToolAction', { tool: toolName })}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
